import { COLLECTIONS, STATUSES } from '__constants__'
import { deleteDocument, updateDocument } from 'services/api/firebase'
import { generatePath, useHistory } from 'react-router-dom'
import { useEvent, useGetSubadminData } from 'hooks'

import PATHS from 'pages/paths'
import { deleteStorageFile } from 'helpers'
import { message } from 'antd'
import { useMemo } from 'react'
import { useTranslations } from 'contexts/Translation'

/* Path's */
const allPath = PATHS.AUTHENTICATED.NEWSPAPERS_ALL
const createPath =
  PATHS.AUTHENTICATED.NEWSPAPER_CREATE || PATHS.AUTHENTICATED.NEWSPAPER_WIZARD
const editPath = PATHS.AUTHENTICATED.NEWSPAPER_EDIT
const showPath = PATHS.AUTHENTICATED.NEWSPAPER_SHOW

const useNewspaperBaseActions = ({
  newspaperId,
  actions,
  logoUrl,
  isBlocked,
  isWebTippsEnabled,
  webTippsConfigurationId
} = {}) => {
  /* Getting translations instance */
  const { t } = useTranslations()
  /* Getting history instance */
  const history = useHistory()

  // Check if user is sub-admin
  const { isSubadmin } = useGetSubadminData()

  /* Function to handle back click */
  const handleBack = useEvent(() => history.goBack())

  /* Handling opening of the create page */
  const handleCreate = useEvent(() => {
    history.push(createPath)
  })

  /* Handling opening of the show page */
  const handleOpen = useEvent(() => {
    const pathParams = { newspaperId }
    const path = generatePath(showPath, pathParams)
    history.push(path)
  })

  /* Handling opening of the edit page or change view to edit mode */
  const handleEdit = useEvent(() => {
    const pathParams = { newspaperId }
    const path = generatePath(editPath, pathParams)
    history.push(path)
  })

  const handleDelete = useEvent(async () => {
    try {
      await deleteDocument(COLLECTIONS.NEWSPAPERS, newspaperId)
      logoUrl && (await deleteStorageFile(logoUrl))
      history.push(allPath)
      message.success(t('Newspaper successfully deleted'))
    } catch (error) {
      message.error(error.message)
    }
  })

  const handleBlock = useEvent(async () => {
    const statusActive = STATUSES.NEWSPAPER_STATUSES.ACTIVE
    const statusBlocked = STATUSES.NEWSPAPER_STATUSES.BLOCKED

    const computedStatus = isBlocked ? statusActive : statusBlocked

    try {
      await updateDocument(COLLECTIONS.NEWSPAPERS, newspaperId, {
        isBlocked: isBlocked ? false : true,
        status: computedStatus
      })

      message.success(t(`Newspaper successfully ${computedStatus}`))
    } catch (error) {
      message.error(t(`Error ${computedStatus} newspaper`))
    }
  })

  const handleEnableWebTipps = useEvent(async () => {
    try {
      await updateDocument(
        COLLECTIONS.WEB_TIPPS_CONFIGURATIONS,
        webTippsConfigurationId,
        {
          isWebTippsEnabled: isWebTippsEnabled ? false : true
        }
      )
    } catch (error) {
      message.error(t(`Webb tipps was updated with error`))
    }
  })
  return useMemo(
    () => ({
      onBack: handleBack,
      onCreate: handleCreate,
      onEdit: handleEdit,
      onDelete: !isSubadmin && handleDelete,
      onBlock: !isSubadmin && handleBlock,
      onOpen: handleOpen,
      onEnableWebTipps: !isSubadmin && handleEnableWebTipps,
      ...(actions || {}),
      createPath,
      editPath,
      showPath
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actions?.onBack,
      actions?.onCreate,
      actions?.onDelete,
      actions?.onEdit,
      actions?.onOpen,
      actions?.onBlock,
      actions?.onEnableWebTipps,
      handleBack,
      handleCreate,
      handleDelete,
      handleEdit,
      handleOpen,
      handleBlock,
      handleEnableWebTipps
    ]
  )
}

export default useNewspaperBaseActions
